import React from "react";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import {
  DeleteButton,
  EditButton,
  List,
  ShowButton,
  useDataGrid,
} from "@refinedev/mui";
import { gemsOf } from "./values";

/** The fields of a pack the shop's order reads. */
type GemPackRow = { sortOrder: number; createdAt: string };

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

/**
 * The v2 gem shop's packs (the `credit-bundles` resource): every row, on sale
 * or not, in the shop's order. The web and the iOS app show the ones on sale
 * within 30 seconds of a save.
 */
export const CreditBundleList = () => {
  // A shop holds a handful of packs: one page, sorted here by the shop's own order.
  const { dataGridProps } = useDataGrid({
    pagination: { pageSize: 100 },
    sorters: { mode: "off" },
  });
  // The shop's order: Order ascending, the older pack first on a tie (the server's findActiveBundles). Sorted here, not by
  // the grid: Refine hands the grid its own (empty) sort model, which overrides any initial sort the grid is given.
  const rows = React.useMemo(
    () =>
      [...((dataGridProps.rows as GemPackRow[] | undefined) ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder || Date.parse(a.createdAt) - Date.parse(b.createdAt)
      ),
    [dataGridProps.rows]
  );

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "sortOrder",
        headerName: "Order",
        type: "number",
        minWidth: 80,
        display: "flex",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "key",
        headerName: "Key",
        minWidth: 140,
        display: "flex",
      },
      {
        field: "name",
        flex: 1,
        headerName: "Name",
        minWidth: 140,
        display: "flex",
      },
      {
        field: "gems",
        headerName: "Gems sold",
        type: "number",
        minWidth: 120,
        display: "flex",
        align: "left",
        headerAlign: "left",
        valueGetter: (_value, row) => gemsOf(row.creditsAmount, row.bonusCredits).gems,
      },
      {
        field: "bonus",
        headerName: "Bonus",
        minWidth: 110,
        display: "flex",
        sortable: false,
        valueGetter: (_value, row) => gemsOf(row.creditsAmount, row.bonusCredits).percent,
        valueFormatter: (value) => (value ? `${value}% more` : "-"),
      },
      {
        field: "priceUsd",
        headerName: "Price (USD)",
        minWidth: 110,
        display: "flex",
        valueFormatter: (value) => `$${value}`,
      },
      {
        field: "isBest",
        headerName: "Best",
        type: "boolean",
        minWidth: 80,
        display: "flex",
      },
      {
        field: "isActive",
        headerName: "On sale",
        type: "boolean",
        minWidth: 90,
        display: "flex",
      },
      {
        field: "stores",
        headerName: "Store products",
        flex: 1,
        minWidth: 180,
        display: "flex",
        sortable: false,
        valueGetter: (_value, row) =>
          [row.appleProductId && `App Store: ${row.appleProductId}`, row.googleProductId && `Play: ${row.googleProductId}`]
            .filter(Boolean)
            .join(" · ") || "-",
      },
      {
        field: "updatedAt",
        flex: 1,
        headerName: "Updated At",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteButton hideText recordItemId={row.id} />
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <List title="Credits Packs">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the v2 gem shop sells on the web and in the iOS app. A pack gives its gems plus its bonus gems; the shop
        lists the packs on sale from the lowest order and picks up a save within 30 seconds. Every purchase is still a
        test purchase. A pack's key cannot change once made: turn a pack off rather than deleting one that has sold.
      </Typography>
      <DataGrid
        {...(dataGridProps as any)}
        rows={rows}
        columns={columns as any}
        sortingMode="client"
      />
    </List>
  );
};
